import React, { useEffect, useMemo, useState } from "react";
import { Color } from "../../helpers";
import { cellTypes } from "./CellTypeList";

import { Cell, State } from "./state/types";
import { Gutters } from "./useGutters";

type Props = Readonly<{
  gutters: Gutters;
  height: number;
  scale: number;
  state: State;
  width: number;
  yOffset: number;
  zoneWidth: number;
  zoneHeight: number;
  zoom: number;
}>;

const clearCanvas = (context: CanvasRenderingContext2D, width: number, height: number) => {
  context.clearRect(0, 0, width, height);
};

const drawSelectionRect = (
  context: CanvasRenderingContext2D,
  scale: number,
  gutters: Gutters,
  startX: number,
  startY: number,
  mouseX: number,
  mouseY: number,) => {
    context.fillStyle = 'rgba(0,0,0,0.5)';
    context.fillRect(
      startX * scale + gutters.left.x + gutters.left.width, 
      startY * scale + gutters.top.height + gutters.top.y, 
      (mouseX - startX) * scale, 
      (mouseY - startY) * scale);
}

const drawBorder = (
  context: CanvasRenderingContext2D,
  color: "red" | "black",
  startX: number,
  endX: number,
  startY: number,
  endY: number,
  implicit: boolean = false,
) => {
  if (implicit) {
    context.setLineDash([3, 3]);
  } 
  context.strokeStyle = color;
  context.beginPath();
  context.moveTo(startX, startY);
  context.lineTo(endX, endY);
  context.stroke();
  context.setLineDash([]);
};

const drawLeftCellBorder = (
  context: CanvasRenderingContext2D,
  cell: Cell,
  scale: number,
  xOffset: number,
  yOffset: number,
  dragOffset: State["dragOffset"],
) => {
  let x = cell.minX * scale + xOffset;
  let startY = cell.minY * scale + yOffset;
  let endY = cell.maxY * scale + yOffset;
  let color: "red" | "black" = "black";
  switch (cell.selectedBorder) {
    case "left":
      color = "red";
      x =
        Math.max(dragOffset.minX, Math.min(dragOffset.maxX, cell.minX + dragOffset.x)) * scale +
        xOffset;
      break;
    case "top":
      startY =
        Math.max(dragOffset.minY, Math.min(dragOffset.maxY, cell.minY + dragOffset.y)) * scale +
        yOffset;
      break;
    case "bottom":
      endY =
        Math.max(dragOffset.minY, Math.min(dragOffset.maxY, cell.maxY + dragOffset.y)) * scale +
        yOffset;
      break;
  }
  drawBorder(context, color, x, x, startY, endY, cell.leftBorderImplicit);
};
const drawRightCellBorder = (
  context: CanvasRenderingContext2D,
  cell: Cell,
  scale: number,
  xOffset: number,
  yOffset: number,
  dragOffset: State["dragOffset"],
) => {
  let x = cell.maxX * scale + xOffset;
  let startY = cell.minY * scale + yOffset;
  let endY = cell.maxY * scale + yOffset;
  let color: "red" | "black" = "black";
  switch (cell.selectedBorder) {
    case "right":
      color = "red";
      x =
        Math.max(dragOffset.minX, Math.min(dragOffset.maxX, cell.maxX + dragOffset.x)) * scale +
        xOffset;
      break;
    case "top":
      startY =
        Math.max(dragOffset.minY, Math.min(dragOffset.maxY, cell.minY + dragOffset.y)) * scale +
        yOffset;
      break;
    case "bottom":
      endY =
        Math.max(dragOffset.minY, Math.min(dragOffset.maxY, cell.maxY + dragOffset.y)) * scale +
        yOffset;
  }
  drawBorder(context, color, x, x, startY, endY, cell.rightBorderImplicit);
};
const drawTopCellBorder = (
  context: CanvasRenderingContext2D,
  cell: Cell,
  scale: number,
  xOffset: number,
  yOffset: number,
  dragOffset: State["dragOffset"],
) => {
  let y = cell.minY * scale + yOffset;
  let startX = cell.minX * scale + xOffset;
  let endX = cell.maxX * scale + xOffset;
  let color: "red" | "black" = "black";
  switch (cell.selectedBorder) {
    case "top":
      color = "red";
      y =
        Math.max(dragOffset.minY, Math.min(dragOffset.maxY, cell.minY + dragOffset.y)) * scale +
        yOffset;
      break;
    case "left":
      startX =
        Math.max(dragOffset.minX, Math.min(dragOffset.maxX, cell.minX + dragOffset.x)) * scale +
        xOffset;
      break;
    case "right":
      endX =
        Math.max(dragOffset.minX, Math.min(dragOffset.maxX, cell.maxX + dragOffset.x)) * scale +
        xOffset;
      break;
  }
  drawBorder(context, color, startX, endX, y, y, cell.topBorderImplicit);
};
const drawBottomCellBorder = (
  context: CanvasRenderingContext2D,
  cell: Cell,
  scale: number,
  xOffset: number,
  yOffset: number,
  dragOffset: State["dragOffset"],
) => {
  let y = cell.maxY * scale + yOffset;
  let startX = cell.minX * scale + xOffset;
  let endX = cell.maxX * scale + xOffset;
  let color: "red" | "black" = "black";
  switch (cell.selectedBorder) {
    case "bottom":
      color = "red";
      y =
        Math.max(dragOffset.minY, Math.min(dragOffset.maxY, cell.maxY + dragOffset.y)) * scale +
        yOffset;
      break;
    case "left":
      startX =
        Math.max(dragOffset.minX, Math.min(dragOffset.maxX, cell.minX + dragOffset.x)) * scale +
        xOffset;
      break;
    case "right":
      endX =
        Math.max(dragOffset.minX, Math.min(dragOffset.maxX, cell.maxX + dragOffset.x)) * scale +
        xOffset;
      break;
  }
  drawBorder(context, color, startX, endX, y, y, cell.bottomBorderImplicit);
};

// tslint:disable-next-line: cyclomatic-complexity
const drawCells = (
  context: CanvasRenderingContext2D,
  cells: State["cells"],
  gutters: Gutters,
  scale: number,
  zoneWidth: number,
  zoneHeight: number,
  dragOffset: State["dragOffset"],
) => {
  context.lineWidth = 1;
  const xOffset = gutters.left.x + gutters.left.width;
  const yOffset = gutters.top.y + gutters.top.height;

  for (let i = 0; i < cells.length; i++) {
    const cell = cells[i];
    // draw the four borders
    if (cell.minX > 0) {
      drawLeftCellBorder(context, cell, scale, xOffset, yOffset, dragOffset);
    }
    if (cell.minY > 0) {
      drawTopCellBorder(context, cell, scale, xOffset, yOffset, dragOffset);
    }
    if (cell.maxX < zoneWidth) {
      drawRightCellBorder(context, cell, scale, xOffset, yOffset, dragOffset);
    }
    if (cell.maxY < zoneHeight) {
      drawBottomCellBorder(context, cell, scale, xOffset, yOffset, dragOffset);
    }

    const x =
      (cell.minX) * scale +
      xOffset + 1;
    const y =
      (cell.minY) * scale +
      yOffset + 1;
      
    if (cell.cellType) {
      let cellColor : Color;
      Object.keys(cellTypes).forEach((key) => {
        let cellType = cellTypes[key].find(cellType => cellType.label === cell.cellType)
        if (cellType) {
          cellColor = cellType.color;
        }
      });
      if (cellColor?.alpha > 0) {
        context.fillStyle = cellColor.toHexString();
        context.globalAlpha = 0.3;
        context.fillRect(x, y, ((cell.maxX - cell.minX) * scale) - 1, ((cell.maxY - cell.minY) * scale) - 1)
        context.globalAlpha = 1;
        context.fillStyle = 'black';
      }
    }
    if (cell.selected) {
      context.lineWidth=3;
      context.strokeStyle = "black";
      context.strokeRect(x, y, ((cell.maxX - cell.minX) * scale) - 1, ((cell.maxY - cell.minY) * scale) - 1)
      context.lineWidth=1;
    }
  }
};

const drawNewColumn = (
  context: CanvasRenderingContext2D,
  position: State["mousePosition"],
  withinGutter: State["withinGutter"],
  gutters: Gutters,
) => {
  if (!position || withinGutter !== "horizontal") {
    return;
  }
  context.strokeStyle = "blue";
  context.lineWidth = 1.5;
  context.beginPath();
  context.moveTo(position[0], gutters.top.y);
  context.lineTo(position[0], gutters.bottom.y + gutters.bottom.height);
  context.stroke();
};

const drawNewRow = (
  context: CanvasRenderingContext2D,
  position: State["mousePosition"],
  withinGutter: State["withinGutter"],
  gutters: Gutters,
) => {
  if (!position || withinGutter !== "vertical") {
    return;
  }
  context.strokeStyle = "blue";
  context.lineWidth = 1.5;
  context.beginPath();
  context.moveTo(gutters.left.x, position[1]);
  context.lineTo(gutters.right.x + gutters.right.width, position[1]);
  context.stroke();
};

const useDrawRowsAndCols = (
  canvas: HTMLCanvasElement | null,
  gutters: Gutters,
  width: number,
  height: number,
  scale: number,
  state: State,
  zoneWidth: number,
  zoneHeight: number,
) => {
  useEffect(() => {
    if (!canvas) {
      return;
    }
    const context = canvas.getContext("2d");
    if (!context) {
      console.error("Unable to get a canvas context.");
      return;
    }
    try {
      clearCanvas(context, width, height);
      if (state.showTableUI) {
        drawCells(
          context,
          state.cells,
          gutters,
          scale,
          zoneWidth,
          zoneHeight,
          state.dragOffset,
        );
      }       
      drawNewColumn(context, state.mousePosition, state.withinGutter, gutters);
      drawNewRow(context, state.mousePosition, state.withinGutter, gutters);
      if (state.mousePosition && state.mouseDragStart && !state.cells.find(x => x.selectedBorder)) {
        drawSelectionRect(
          context, 
          scale, 
          gutters, 
          state.mouseDragStart[0], 
          state.mouseDragStart[1], 
          state.mousePosition[0], 
          state.mousePosition[1]);
      }
    } catch (err) {
      console.error(err);
    }
  });
};

export const RowsAndCols = ({
  gutters,
  height,
  scale,
  state,
  width,
  zoneHeight,
  zoneWidth,
  zoom,
}: Props) => {
  const [canvasRef, setCanvasRef] = useState<HTMLCanvasElement | null>(null);
  useDrawRowsAndCols(canvasRef, gutters, width, height, scale, state, zoneWidth, zoneHeight);
  const canvasStyles = useMemo(
    () => ({
      height: `${height * zoom}px`,
      width: `${width * zoom}px`,
    }),
    [height, width, zoom],
  );
  return (
    <canvas
      className="overlay"
      height={height}
      ref={setCanvasRef}
      style={canvasStyles}
      width={width}
    />
  );
};
RowsAndCols.displayName = "RowsAndCols";
